import { cn } from "@chams-pay/design-system/lib/utils";
import { Actions, Sheet } from "konsta/react";
import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useAppBackClose } from "./app-feedback.js";

interface DragState {
  readonly pointerId: number;
  readonly startY: number;
  readonly lastY: number;
  readonly lastAt: number;
  readonly velocity: number;
}

interface DrawerDrag {
  readonly dragging: boolean;
  readonly offset: number;
  readonly onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

function useDrawerDrag(opened: boolean, onClose: () => void): DrawerDrag {
  const gesture = useRef<DragState | null>(null);
  const offsetRef = useRef(0);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (opened) return;
    gesture.current = null;
    offsetRef.current = 0;
    setOffset(0);
    setDragging(false);
  }, [opened]);

  const reset = () => {
    gesture.current = null;
    offsetRef.current = 0;
    setOffset(0);
    setDragging(false);
  };

  const finish = (event: ReactPointerEvent<HTMLElement>, cancelled = false) => {
    const active = gesture.current;
    if (!active || active.pointerId !== event.pointerId) return;
    const shouldClose =
      !cancelled && (offsetRef.current >= 88 || (offsetRef.current >= 24 && active.velocity > 0.5));
    reset();
    if (shouldClose) onClose();
  };

  return {
    dragging,
    offset,
    onPointerDown: (event) => {
      if (!opened || event.isPrimary === false) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      gesture.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        lastY: event.clientY,
        lastAt: event.timeStamp,
        velocity: 0,
      };
      setDragging(true);
    },
    onPointerMove: (event) => {
      const active = gesture.current;
      if (!active || active.pointerId !== event.pointerId) return;
      const next = Math.max(0, event.clientY - active.startY);
      const elapsed = Math.max(1, event.timeStamp - active.lastAt);
      gesture.current = {
        ...active,
        lastY: event.clientY,
        lastAt: event.timeStamp,
        velocity: (event.clientY - active.lastY) / elapsed,
      };
      offsetRef.current = next;
      setOffset(next);
    },
    onPointerUp: (event) => finish(event),
    onPointerCancel: (event) => finish(event, true),
  };
}

function startsDrawerDrag(event: ReactPointerEvent<HTMLElement>): boolean {
  const target = event.target;
  return (
    target instanceof Element &&
    Boolean(target.closest("[data-mobile-drawer-drag-zone]")) &&
    !target.closest("button,a,input,textarea,select,[role='button']")
  );
}

export function MobileDrawerHandle() {
  return (
    <div
      aria-hidden="true"
      data-mobile-drawer-handle=""
      data-mobile-drawer-drag-zone=""
      className="mobile-drawer-handle relative z-10 flex h-6 shrink-0 touch-none cursor-grab items-center justify-center active:cursor-grabbing"
    >
      <span className="h-1 w-10 rounded-full bg-border transition-colors group-data-[dragging=true]:bg-primary" />
    </div>
  );
}

type DragStyle = CSSProperties & { readonly "--mobile-drawer-drag-y": string };

export function MobileDraggableSheet({
  opened = false,
  onClose,
  className,
  style,
  children,
  withHandle = true,
  ...props
}: ComponentProps<typeof Sheet> & {
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly withHandle?: boolean;
}) {
  const drag = useDrawerDrag(opened, onClose);
  useAppBackClose(opened, onClose);
  const dragStyle: DragStyle = {
    ...style,
    "--mobile-drawer-drag-y": `${drag.offset}px`,
  };

  return (
    <Sheet
      {...props}
      opened={opened}
      data-dragging={drag.dragging}
      className={cn("mobile-draggable-sheet mobile-period-drawer-style group", className)}
      style={dragStyle}
      onPointerDown={(event) => {
        if (startsDrawerDrag(event)) drag.onPointerDown(event);
      }}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
    >
      {withHandle && <MobileDrawerHandle />}
      {children}
    </Sheet>
  );
}

export function MobileDraggableActions({
  opened = false,
  onClose,
  className,
  style,
  children,
  ...props
}: ComponentProps<typeof Actions> & {
  readonly onClose: () => void;
  readonly children: ReactNode;
}) {
  const drag = useDrawerDrag(opened, onClose);
  useAppBackClose(opened, onClose);
  const dragStyle: DragStyle = {
    ...style,
    "--mobile-drawer-drag-y": `${drag.offset}px`,
  };

  return (
    <Actions
      {...props}
      opened={opened}
      data-dragging={drag.dragging}
      className={cn("mobile-draggable-actions mobile-period-drawer-style group", className)}
      style={dragStyle}
      onPointerDown={(event) => {
        if (startsDrawerDrag(event)) drag.onPointerDown(event);
      }}
      onPointerMove={drag.onPointerMove}
      onPointerUp={drag.onPointerUp}
      onPointerCancel={drag.onPointerCancel}
    >
      <MobileDrawerHandle />
      {children}
    </Actions>
  );
}
